import React, { useState, useRef, useEffect } from "react";
import { userService } from "../../../_services";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import * as z from 'zod';

const addUserLinkSchema = z.object({
    assignedUser: z.string().min(3, 'Please select user'),
    repId: z.coerce.string().min(1, 'Please select user'),
    groupNumberId: z.coerce.string().min(1, 'Please select group number'),
    linkType: z.string().min(1, 'Link Type must be selected'),
    linkSuffix: z.string()
        .min(3, 'Link Suffix must be minimum 3 and maximum 16 characters long')
        .max(16, 'Link Suffix must be minimum 3 and maximum 16 characters long'),
});

/**
 * Add Secondary Link Component
 * Modernized with shadcn components - No Dialog wrapper, parent handles Dialog
 * Includes typeahead user search and dynamic group number dropdown
 */
function AddUserLink({ adduserclose, updateUserList }) {
    // Form fields
    const [assignedUser, setAssignedUser] = useState('');
    const [repId, setRepId] = useState('');
    const [groupNumber, setGroupNumber] = useState('');
    const [groupNumberId, setGroupNumberId] = useState('');
    const [groupOptions, setGroupOptions] = useState([]); // [{opt, val}]
    const [linkType, setLinkType] = useState('Perpetual');
    const [linkSuffix, setLinkSuffix] = useState('');

    // Typeahead
    const [assignedUserList, setAssignedUserList] = useState([]);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [prevAssignedUser, setPrevAssignedUser] = useState('');
    const dropdownRef = useRef(null);
    const groupFieldRef = useRef(null);

    // UI state
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errors, setErrors] = useState({
        AssignedUser_E: '',
        GroupNo_E: '',
        LinkType_E: '',
        LinkSuffix_E: ''
    });

    // Close typeahead dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowUserDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Typeahead: fetch matching users when 3+ chars typed
    const handleAssignedUserKeyUp = async () => {
        const stext = assignedUser;
        if (stext.length >= 3 && stext !== prevAssignedUser) {
            setSearchLoading(true);
            setShowUserDropdown(true);
            try {
                const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                const [userId, userName] = Usermeta_Data.split(':');
                const apidata = await userService.GetTypeaheadUsername(userId, userName, stext);
                if (apidata && apidata.length > 0) {
                    setAssignedUserList(apidata);
                    setShowUserDropdown(true);
                } else {
                    setAssignedUserList([]);
                    setShowUserDropdown(false);
                }
            } catch (err) {
                console.error('Error fetching typeahead:', err);
                setAssignedUserList([]);
                setShowUserDropdown(false);
            } finally {
                setSearchLoading(false);
            }
        } else if (stext.length < 3) {
            setAssignedUserList([]);
            setShowUserDropdown(false);
            setSearchLoading(false);
        }
    };

    const handleAssignedUserChange = (e) => {
        const value = e.target.value;
        setAssignedUser(value);
        // Reset group fields when user text changes
        setGroupOptions([]);
        setGroupNumber('');
        setGroupNumberId('');
        setRepId('');
        setErrors(prev => ({ ...prev, AssignedUser_E: '' }));
    };

    // Select a user from the typeahead dropdown
    const handleSelectUser = (selectedUser) => {
        setAssignedUser(selectedUser.userName.trim());
        setPrevAssignedUser(selectedUser.userName.trim());
        setRepId(String(selectedUser.userId));
        setAssignedUserList([]);
        setShowUserDropdown(false);

        // Parse group numbers into options
        if (selectedUser.groupNumber && selectedUser.groupNumberId) {
            const groupNos = selectedUser.groupNumber.split(',');
            const gnIds = selectedUser.groupNumberId.split(',');
            const options = [];
            for (let i = 0; i < groupNos.length; i++) {
                options.push({
                    opt: groupNos[i].trim(),
                    val: gnIds[i].trim()
                });
            }
            setGroupOptions(options);

            // Auto-select first group
            if (options.length > 0) {
                setGroupNumber(options[0].opt);
                setGroupNumberId(options[0].val);
            }
        } else {
            setGroupOptions([]);
            setGroupNumber('');
            setGroupNumberId('');
        }

        setErrors(prev => ({ ...prev, AssignedUser_E: '', GroupNo_E: '' }));

        // Focus group field
        setTimeout(() => groupFieldRef.current?.focus(), 100);
    };

    // Select a group from the dropdown
    const handleGroupChange = (val) => {
        const selected = groupOptions.find(g => g.val === val);
        if (selected) {
            setGroupNumber(selected.opt);
            setGroupNumberId(selected.val);
            setErrors(prev => ({ ...prev, GroupNo_E: '' }));
        }
    };

    const handleLinkSuffixChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^[a-zA-Z0-9]+$/.test(value)) {
            setLinkSuffix(value);
            setErrors(prev => ({ ...prev, LinkSuffix_E: '' }));
        }
    };

    // Per-field validation matching old component
    const validateForm = () => {
        const result = addUserLinkSchema.safeParse({ assignedUser, repId, groupNumberId, linkType, linkSuffix });
        const fe = result.success ? {} : result.error.flatten().fieldErrors;
        const newErrors = {
            AssignedUser_E: fe.assignedUser?.[0] || fe.repId?.[0] || '',
            GroupNo_E: fe.groupNumberId?.[0] || '',
            LinkType_E: fe.linkType?.[0] || '',
            LinkSuffix_E: fe.linkSuffix?.[0] || '',
        };
        setErrors(newErrors);
        return result.success;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage('');

        if (!validateForm()) return;

        setLoading(true);

        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');

            const apidata = await userService.SaveSecondLink(
                userId,
                userName,
                repId,
                linkType,
                linkSuffix,
                groupNumberId
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].statusCode !== -1) {
                    setSuccessMessage(apidata[0].statusMessage || 'Link added successfully');
                    updateUserList();
                    adduserclose();
                } else {
                    setSuccessMessage(apidata[0].statusMessage || 'Failed to add link');
                }
            } else {
                setSuccessMessage('Please try later');
            }
        } catch (err) {
            console.error('Error adding link:', err);
            setSuccessMessage('Please try later');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                {/* Assigned User - Typeahead */}
                <div className="space-y-2 relative" ref={dropdownRef}>
                    <Label htmlFor="assignedUser">
                        Assigned User <span className="text-destructive">*</span>
                    </Label>
                    <Input
                        id="assignedUser"
                        autoComplete="off"
                        value={assignedUser}
                        onChange={handleAssignedUserChange}
                        onKeyUp={handleAssignedUserKeyUp}
                        placeholder="Type 3+ characters to search..."
                        className={errors.AssignedUser_E ? 'border-destructive' : ''}
                        autoComplete="off"
                    />
                    {errors.AssignedUser_E && (
                        <p className="text-xs text-destructive">{errors.AssignedUser_E}</p>
                    )}
                    {/* Typeahead dropdown */}
                    {showUserDropdown && (
                        <ul className="absolute z-50 top-full left-0 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {searchLoading ? (
                                <li className="px-3 py-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Searching...
                                </li>
                            ) : assignedUserList.length > 0 ? (
                                assignedUserList.map((item, idx) => (
                                    <li
                                        key={item.userName + '_' + idx}
                                        className="px-3 py-2 text-sm cursor-pointer hover:bg-muted transition-colors"
                                        onClick={() => handleSelectUser(item)}
                                    >
                                        {item.userName}
                                    </li>
                                ))
                            ) : (
                                <li className="px-3 py-2 text-sm text-muted-foreground">No users found</li>
                            )}
                        </ul>
                    )}
                </div>

                {/* Group Number - Dynamic dropdown from selected user */}
                <div className="space-y-2">
                    <Label htmlFor="groupNumber">
                        Group Number <span className="text-destructive">*</span>
                    </Label>
                    {groupOptions.length > 0 ? (
                        <Select
                            value={groupNumberId}
                            onValueChange={handleGroupChange}
                        >
                            <SelectTrigger ref={groupFieldRef} className={errors.GroupNo_E ? 'border-destructive' : ''}>
                                <SelectValue placeholder="Select group number" />
                            </SelectTrigger>
                            <SelectContent>
                                {groupOptions.map((g) => (
                                    <SelectItem key={g.val} value={g.val}>
                                        {g.opt}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            ref={groupFieldRef}
                            id="groupNumber"
                            value={groupNumber}
                            readOnly
                            placeholder="Select a user first"
                            className={`bg-gray-50 ${errors.GroupNo_E ? 'border-destructive' : ''}`}
                        />
                    )}
                    {errors.GroupNo_E && (
                        <p className="text-xs text-destructive">{errors.GroupNo_E}</p>
                    )}
                </div>

                {/* Link Type */}
                <div className="space-y-2">
                    <Label htmlFor="linkType">
                        Link Type <span className="text-destructive">*</span>
                    </Label>
                    <Select value={linkType} onValueChange={setLinkType}>
                        <SelectTrigger className={errors.LinkType_E ? 'border-destructive' : ''}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Perpetual">Perpetual</SelectItem>
                        </SelectContent>
                    </Select>
                    {errors.LinkType_E && (
                        <p className="text-xs text-destructive">{errors.LinkType_E}</p>
                    )}
                </div>

                {/* Link Suffix */}
                <div className="space-y-2">
                    <Label htmlFor="linkSuffix">
                        Link Suffix <span className="text-destructive">*</span>
                    </Label>
                    <Input
                        id="linkSuffix"
                        value={linkSuffix}
                        onChange={handleLinkSuffixChange}
                        placeholder="Enter 3-16 alphanumeric characters"
                        maxLength={16}
                        className={errors.LinkSuffix_E ? 'border-destructive' : ''}
                    />
                    <p className="text-xs text-muted-foreground">
                        {linkSuffix.length}/16 characters (alphanumeric only)
                    </p>
                    {errors.LinkSuffix_E && (
                        <p className="text-xs text-destructive">{errors.LinkSuffix_E}</p>
                    )}
                </div>
            </div>

            {/* Success / Error Message */}
            {successMessage && (
                <div className="p-3 bg-muted border border-border rounded-md">
                    <p className="text-sm text-primary">{successMessage}</p>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button
                    type="button"
                    variant="outline"
                    onClick={adduserclose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="secondary"
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Adding...
                        </>
                    ) : (
                        'Add Link'
                    )}
                </Button>
            </div>
        </form>
    );
}

export default AddUserLink;


